/* =========================================================================
   "From guys who bought it", filled from Instagram

   What a feed widget actually does, and what this does: ask the account's
   own media endpoint for its latest posts and play the videos among them.
   The part widgets do not advertise is that it cannot be done from the page.
   Reading an account's media needs a long-lived access token, and a token in
   the page is a token anybody can copy out of it — so ours sits in an
   environment variable on the backend the login sheet already talks to. That
   backend calls graph.instagram.com and hands back a short list of public
   URLs. This file only ever sees the list.

   It fills the cards the section already rendered, rather than building new
   ones: same markup, same classes, same frame, so the rail looks exactly as
   it did and the design is not this file's business. Each card keeps the
   product name and price the merchant typed, because Instagram has no idea
   what they are.

   Everything here is additive and every branch has somewhere to fall back
   to. No path configured, endpoint down, endpoint slow, a response that is
   not what we expect, fewer reels than cards — in every one of those the
   rail is the uploaded-reel rail the section rendered, which is itself the
   poster rail it was before that. Nothing on this page depends on Instagram
   being reachable.

   Instagram's own CDN links are signed and expire, so they are not stored:
   the endpoint caches its Instagram response for a short while and hands out
   fresh URLs, and this keeps its copy only for the length of a browsing
   session.
   ========================================================================= */
(function () {
  'use strict';

  if (window.VroInstagramFeed) return;

  var CACHE_KEY = 'vro_ig_reels:';
  /* Long enough that moving around the site does not re-ask, short enough
     that a signed CDN link handed out at the start of a session has not gone
     stale by the end of it. */
  var CACHE_MS = 10 * 60 * 1000;
  var TIMEOUT_MS = 6000;

  function sections() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-vro-ig-feed]'));
  }

  function readCache(url) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY + url);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.at || Date.now() - saved.at > CACHE_MS) return null;
      return saved.data;
    } catch (e) { return null; }
  }

  function writeCache(url, data) {
    try {
      sessionStorage.setItem(CACHE_KEY + url, JSON.stringify({ at: Date.now(), data: data }));
    } catch (e) { /* private mode, or the quota */ }
  }

  /* A slow endpoint must not be a slow page. The rail is already rendered
     and already works; this either arrives in time to improve it or it does
     not arrive. */
  function ask(url) {
    var cached = readCache(url);
    if (cached) return Promise.resolve(cached);

    var controller = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT_MS);

    /* No request headers on purpose.

       An Accept header makes this a request the browser must ask
       permission for first — it sends an OPTIONS preflight and will not
       make the real call until that is answered with CORS headers of its
       own. The reels endpoint is a worker on another origin that answers
       GET and nothing else, so the preflight went unanswered, the fetch
       was rejected before it left, and this file did what it does with
       any failure: nothing, quietly, leaving the rail on its stills.

       Without headers it is a simple cross-origin GET, which needs no
       preflight. Same request the reels section makes, which is the one
       known to work against this endpoint. */
    return fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('feed ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var reels = normalise(data);
        if (reels.length) writeCache(url, reels);
        return reels;
      })
      .catch(function () { return []; })
      .then(function (reels) { clearTimeout(timer); return reels; });
  }

  /* The endpoint's shape is ours to define, but being strict about reading
     it costs nothing and means a half-written response cannot put a broken
     card on the page. Accepts {reels:[...]}, {data:[...]} or a bare array,
     and keeps only entries that actually name a video. */
  function normalise(data) {
    var list = [];
    if (Array.isArray(data)) list = data;
    else if (data && Array.isArray(data.reels)) list = data.reels;
    else if (data && Array.isArray(data.data)) list = data.data;

    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i] || {};
      var video = item.video || item.media_url || item.video_url || '';
      if (typeof video !== 'string' || !/^https:\/\//.test(video)) continue;
      out.push({
        video: video,
        poster: pick(item.poster, item.thumbnail_url, item.thumbnail),
        permalink: pick(item.permalink, item.link, item.url),
        username: pick(item.username, item.handle),
        caption: pick(item.caption)
      });
    }
    return out;
  }

  function pick() {
    for (var i = 0; i < arguments.length; i++) {
      var value = arguments[i];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  function https(url) {
    return typeof url === 'string' && /^https:\/\//.test(url) ? url : '';
  }

  /* ---------------------------------------------------------------
     Filling a card
  --------------------------------------------------------------- */

  function fillCard(card, reel, options) {
    var video = card.querySelector('.community-clip-video');
    if (video) {
      video.setAttribute('data-vro-clip-src', reel.video);
      /* Whatever it had loaded is the wrong clip now. */
      if (video.getAttribute('src') && video.getAttribute('src') !== reel.video) {
        video.removeAttribute('src');
        try { video.load(); } catch (e) { /* jsdom, old Safari */ }
      }
      var poster = https(reel.poster);
      if (poster) video.setAttribute('poster', poster);
    }

    /* The still underneath, so a card that has not started playing is this
       reel rather than the last thing the merchant uploaded. */
    var still = card.querySelector('.community-clip-poster img');
    var shot = https(reel.poster);
    if (still && shot) {
      still.setAttribute('src', shot);
      /* srcset would keep winning over the src we just set. */
      still.removeAttribute('srcset');
      still.removeAttribute('sizes');
    }

    /* Tapping the card goes to the post it came from. */
    var permalink = https(reel.permalink);
    if (permalink) {
      card.setAttribute('href', permalink);
      card.setAttribute('target', '_blank');
      card.setAttribute('rel', 'noopener');
    }

    if (options.useHandle && reel.username) {
      var handle = card.querySelector('.community-clip-handle');
      if (handle) handle.textContent = '@' + reel.username.replace(/^@/, '');
    }

    card.setAttribute('data-vro-ig-filled', '');
    card.hidden = false;
  }

  /* A card with no reel of its own. The feed is what fills these, so with
     nothing to fill them with they are frames around nothing. */
  function isEmptyCard(card) {
    var own = card.querySelector('.community-clip-video');
    var hasOwn = own && (own.getAttribute('data-vro-clip-src') || '').trim();
    if (hasOwn) return false;
    /* A thumbnail is content too: the block has a picture even if it has no
       reel, and that card is not empty. */
    return !card.querySelector('.community-clip-poster img');
  }

  function hideEmpty(section) {
    Array.prototype.forEach.call(
      section.querySelectorAll('.community-clip-card'),
      function (card) { if (isEmptyCard(card)) card.hidden = true; }
    );
  }

  function fill(section, reels) {
    var cards = Array.prototype.slice.call(section.querySelectorAll('.community-clip-card'));
    if (!cards.length || !reels.length) return;

    var options = {
      useHandle: section.hasAttribute('data-vro-ig-use-handle'),
      hideEmpty: section.hasAttribute('data-vro-ig-hide-empty')
    };

    cards.forEach(function (card, i) {
      if (i < reels.length) {
        fillCard(card, reels[i], options);
        return;
      }
      /* Past the end of the feed. A card with a reel or a picture of its
         own is left alone either way — that is real content, not a gap. */
      if (options.hideEmpty && isEmptyCard(card)) card.hidden = true;
    });

    section.setAttribute('data-vro-ig-state', 'filled');

    /* The reels script decides what plays, and what it should consider has
       just changed. */
    if (window.VroClipReels && window.VroClipReels.refresh) window.VroClipReels.refresh();
  }

  /* How many reels are worth asking for: the number of cards there are to
     put them in. Asking for more is work the endpoint does for nothing,
     and the endpoint wants telling — it takes the count as ?limit=. */
  function withLimit(url, count) {
    if (!count) return url;
    try {
      var u = new URL(url, window.location.href);
      u.searchParams.set('limit', String(count));
      return u.href;
    } catch (e) {
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'limit=' + count;
    }
  }

  function load(section) {
    var url = section.getAttribute('data-vro-ig-feed');
    if (!url) return;
    url = withLimit(url, section.querySelectorAll('.community-clip-card').length);
    section.setAttribute('data-vro-ig-state', 'asking');
    ask(url).then(function (reels) {
      if (!reels.length) {
        /* Said out loud in the DOM rather than the console, so this is
           answerable from a screenshot. */
        section.setAttribute('data-vro-ig-state', 'none');
        /* And the cards that were waiting for it are put away, the same as
           the ones past the end of a feed that did answer. An endpoint that
           is down left them standing as empty outlines — the rail's own
           cards have something to show and these never did. */
        if (section.hasAttribute('data-vro-ig-hide-empty')) hideEmpty(section);
        return;
      }
      fill(section, reels);
    });
  }

  function start() {
    var list = sections();
    if (!list.length) return;
    list.forEach(load);
  }

  window.VroInstagramFeed = { load: load, reload: function () { sections().forEach(load); } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
